import { useState } from 'react';
import { Copy, KeyRound, ShieldCheck, UserPlus, Users as UsersIcon } from 'lucide-react';
import { usernameSchema } from '@kick/validation';
import { getErrorDetails, getErrorMessage } from '@/lib/api';
import {
  useCrearUsuarioComercio,
  useMerchantId,
  useUsuariosComercio,
} from '@/lib/queries';
import {
  Boton,
  Campo,
  Cargando,
  Entrada,
  ErrorCarga,
  Modal,
  Seleccion,
  Vacio
} from '@/components/ui';
import { useAvisos } from '@/components/avisos';

export function Usuarios() {
  const merchantId = useMerchantId();
  const usuarios = useUsuariosComercio(merchantId);
  const [creando, setCreando] = useState(false);

  if (usuarios.isLoading) return <Cargando texto="Cargando usuarios…" />;
  if (usuarios.isError) {
    return (
      <ErrorCarga mensaje={getErrorMessage(usuarios.error)} onReintentar={() => usuarios.refetch()} />
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl text-kick-dark">Usuarios</h1>
          <p className="mt-1 text-sm text-kick-gray">
            Quiénes pueden entrar al panel de tu comercio.
          </p>
        </div>
        <Boton icono={<UserPlus className="h-4 w-4" />} onClick={() => setCreando(true)}>
          Nuevo usuario
        </Boton>
      </div>

      {usuarios.data?.length === 0 ? (
        <div className="mt-8">
          <Vacio
            icono={<UsersIcon className="h-6 w-6" />}
            titulo="Sin usuarios adicionales"
            descripcion="Crea cuentas para tus empleados: podrán atender pedidos sin ver tus informes de ventas."
          />
        </div>
      ) : (
        <ul className="mt-6 divide-y divide-kick-gray/10 overflow-hidden rounded-kick bg-white shadow-card">
          {usuarios.data?.map((usuario) => (
            <li key={usuario.id} className="flex items-center gap-3 px-4 py-3.5">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-kick-light text-kick-gray">
                {usuario.rolEnComercio === 'OWNER' ? (
                  <ShieldCheck className="h-5 w-5 text-kick-red" />
                ) : (
                  <UsersIcon className="h-5 w-5" />
                )}
              </div>

              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-kick-ink">{usuario.fullName}</p>
                <p className="truncate font-mono text-sm text-kick-gray">{usuario.username}</p>
              </div>

              <div className="shrink-0 text-right">
                <span className="rounded-full bg-kick-light px-2.5 py-1 text-xs font-semibold text-kick-gray">
                  {usuario.rolEnComercio === 'OWNER' ? 'Propietario' : 'Empleado'}
                </span>
                <p className="mt-1 text-xs text-kick-gray">
                  {usuario.lastLoginAt
                    ? `Último ingreso: ${new Date(usuario.lastLoginAt).toLocaleDateString('es-CO')}`
                    : 'Nunca ha ingresado'}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}

      {creando && <ModalNuevoUsuario merchantId={merchantId} onCerrar={() => setCreando(false)} />}
    </div>
  );
}

function ModalNuevoUsuario({
  merchantId,
  onCerrar,
}: {
  merchantId: string;
  onCerrar: () => void;
}) {
  const crear = useCrearUsuarioComercio(merchantId);
  const avisos = useAvisos();

  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<'OWNER' | 'STAFF'>('STAFF');
  const [errores, setErrores] = useState<Record<string, string>>({});

  /** Credenciales recién creadas: se muestran una sola vez. */
  const [credenciales, setCredenciales] = useState<{
    username: string;
    passwordTemporal: string;
  } | null>(null);

  const enviar = (e: React.FormEvent): void => {
    e.preventDefault();
    setErrores({});

    const revision = usernameSchema.safeParse(username);
    if (!revision.success) {
      setErrores({ username: revision.error.issues[0]?.message ?? 'Usuario inválido' });
      return;
    }

    crear.mutate(
      { username: revision.data, fullName: fullName.trim(), phone: phone.trim(), role },
      {
        onSuccess: setCredenciales,
        onError: (error) => {
          const detalles = getErrorDetails(error);
          setErrores(
            Object.fromEntries(Object.entries(detalles).map(([k, v]) => [k, v[0] ?? ''])),
          );
          avisos.error(getErrorMessage(error));
        },
      },
    );
  };

  // Pantalla de credenciales: no se puede volver a ver la contraseña.
  if (credenciales) {
    return (
      <Modal
        abierto
        onCerrar={onCerrar}
        titulo="Usuario creado"
        descripcion="Anota estos datos: la contraseña no se vuelve a mostrar."
        ancho="sm"
      >
        <div className="p-5">
          <div className="rounded-kick border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-start gap-2">
              <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
              <p className="text-sm text-amber-800">
                Entrégale estos datos a la persona. Deberá cambiar la contraseña al ingresar.
              </p>
            </div>

            <dl className="mt-4 space-y-2">
              <DatoCopiable etiqueta="Usuario" valor={credenciales.username} />
              <DatoCopiable etiqueta="Contraseña temporal" valor={credenciales.passwordTemporal} />
            </dl>
          </div>

          <div className="mt-5 flex justify-end">
            <Boton onClick={onCerrar}>Entendido</Boton>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo="Nuevo usuario"
      descripcion="Se generará una contraseña temporal."
      ancho="sm"
    >
      <form onSubmit={enviar} className="space-y-4 p-5">
        <Campo etiqueta="Nombre completo" requerido error={errores.fullName}>
          <Entrada
            autoFocus
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Ana María López"
            maxLength={150}
          />
        </Campo>

        <Campo
          etiqueta="Usuario"
          requerido
          error={errores.username}
          ayuda="Con esto ingresará al panel. Minúsculas, números, punto o guion."
        >
          <Entrada
            value={username}
            onChange={(e) => setUsername(e.target.value.toLowerCase())}
            placeholder="analopez"
            autoCapitalize="none"
            spellCheck={false}
            error={!!errores.username}
          />
        </Campo>

        <Campo etiqueta="Celular" requerido error={errores.phone}>
          <Entrada
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="3105551234"
            inputMode="numeric"
            error={!!errores.phone}
          />
        </Campo>

        <Campo
          etiqueta="Rol"
          ayuda={
            role === 'STAFF'
              ? 'El empleado atiende pedidos y edita el menú, pero no ve informes ni usuarios.'
              : 'El propietario tiene acceso completo al comercio.'
          }
        >
          <Seleccion value={role} onChange={(e) => setRole(e.target.value as 'OWNER' | 'STAFF')}>
            <option value="STAFF">Empleado</option>
            <option value="OWNER">Propietario</option>
          </Seleccion>
        </Campo>

        <div className="flex justify-end gap-2 pt-2">
          <Boton type="button" variante="secundario" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton type="submit" cargando={crear.isPending}>
            Crear usuario
          </Boton>
        </div>
      </form>
    </Modal>
  );
}

function DatoCopiable({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  const avisos = useAvisos();

  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-amber-700">{etiqueta}</dt>
      <dd className="mt-0.5 flex items-center gap-2">
        <code className="flex-1 rounded bg-white px-2.5 py-1.5 font-mono text-sm text-kick-ink">
          {valor}
        </code>
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard.writeText(valor);
            avisos.exito(`${etiqueta} copiado`);
          }}
          aria-label={`Copiar ${etiqueta}`}
          className="rounded-kick p-2 text-amber-700 transition hover:bg-amber-100"
        >
          <Copy className="h-4 w-4" />
        </button>
      </dd>
    </div>
  );
}
